import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    IconButton,
    Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const CancelDocumentsModal = ({ open, onClose, direction }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showContent, setShowContent] = useState(false);
    const [pulseAnimation, setPulseAnimation] = useState(false);

    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => setShowContent(true), 300);
            const pulseTimer = setTimeout(() => setPulseAnimation(true), 1000);
            return () => {
                clearTimeout(timer);
                clearTimeout(pulseTimer);
            };
        } else {
            setShowContent(false);
            setPulseAnimation(false);
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    minHeight: '500px',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)',
                        backgroundSize: '400% 100%',
                        animation: 'gradientShift 3s ease infinite'
                    }
                }
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: 'blur(4px)'
                },
                '@keyframes gradientShift': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' }
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{ 
                textAlign: 'center', 
                pb: 1, 
                pt: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                position: 'relative'
            }}>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 14,
                        top: 4,
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                    }}
                >
                    <CloseIcon />
                </IconButton>
                
                <Zoom in={showContent} timeout={500}>
                    <Box dir={direction} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CancelIcon sx={{ fontSize: 28, color: '#ff6b6b' }} />
                        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    {t('CancelDocuments')}
                </Typography>
                    </Box>
                </Zoom>
            </DialogTitle>
            
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
            
            {/* Content */}
            <DialogContent sx={{ textAlign: 'center', py: 2, px: 2 }}>
                <Fade in={showContent} timeout={700}>
                    <Box>
                        {/* Main Instruction */}
                <Box sx={{ mb: 4 }}>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    fontSize: '18px', 
                                    lineHeight: 1.8, 
                                    mb: 3,
                                    color: '#2c3e50',
                                    fontWeight: 500,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                            >
                                {t('CancelDocumentsInstruction')}
                    </Typography>
                            
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: '#7f8c8d',
                                    mb: 4,
                                    fontStyle: 'italic'
                                }}
                            >
                                {t('CancelDocumentsSubInstruction')}
                            </Typography>
                        </Box>
                        
                        {/* Interactive Demo */}
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            mt: 4,
                            p: 3,
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: 3,
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                        }}>
                            {/* Document Row Demo */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {/* Document Info */}
                                <Box dir={direction} sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2,
                                    p: 2,
                                    background: '#f8f9fa',
                                    borderRadius: 2,
                                    border: '1px solid #e9ecef',
                                    minWidth: '200px'
                                }}>
                                    <Box sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        background: '#6c757d',
                                        borderRadius: 1
                                    }} />
                                    <Typography variant="body2" sx={{ color: '#495057', fontWeight: 500 }}>
                                        {t('ExampleDocument')}
                                    </Typography>
                                </Box>
                                
                                {/* Animated Arrow */}
                                <Zoom in={showContent} timeout={1000}>
                                    <Box sx={{ 
                                        animation: pulseAnimation ? 'pulse 2s infinite' : 'none',
                                        '@keyframes pulse': {
                                            '0%': { transform: 'scale(1)', opacity: 1 },
                                            '50%': { transform: 'scale(1.1)', opacity: 0.8 },
                                            '100%': { transform: 'scale(1)', opacity: 1 }
                                        }
                                    }}>
                                        <ArrowForwardIcon 
                                            sx={{ 
                                                fontSize: 32, 
                                                color: '#e74c3c',
                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                            }} 
                                        />
                                    </Box>
                                </Zoom>
                                
                                {/* Target Checkbox */}
                                <Zoom in={showContent} timeout={1200}>
                                    <Box sx={{ 
                                        position: 'relative',
                                        animation: pulseAnimation ? 'checkboxPulse 2s infinite' : 'none',
                                        '@keyframes checkboxPulse': {
                                            '0%': { 
                                                transform: 'scale(1)',
                                                boxShadow: '0 0 0 0 rgba(231, 76, 60, 0.7)'
                                            },
                                            '70%': { 
                                                transform: 'scale(1.05)',
                                                boxShadow: '0 0 0 10px rgba(231, 76, 60, 0)'
                                            },
                                            '100%': { 
                                                transform: 'scale(1)',
                                                boxShadow: '0 0 0 0 rgba(231, 76, 60, 0)'
                                            }
                                        }
                                    }}>
                                        <CheckBoxIcon 
                                            sx={{ 
                                                fontSize: 40, 
                                                color: '#e74c3c',
                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                            }} 
                                        />
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                display: 'block',
                                                color: '#e74c3c',
                                                fontWeight: 'bold',
                                                mt: 0.5,
                                                textAlign: 'center'
                                            }}
                                        >
                                            {t('ClickHere')}
                                        </Typography>
                                    </Box>
                                </Zoom>
                            </Box>
                        </Box>
                        
                        {/* Steps */}
                        <Fade in={showContent} timeout={1500}>
                            <Box sx={{ mt: 4 }}>
                                <Typography dir={direction} variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                                    {t('CancellationSteps')}
                                </Typography>
                                <Box dir={direction} sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                    {[
                                        t('CancelStep1'),
                                        t('CancelStep2'),
                                        t('CancelStep3'),
                                        t('CancelStep4')
                                    ].map((step, index) => (
                                        <Box key={index} sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1,
                                            p: 1,
                                            background: 'rgba(255,255,255,0.5)',
                                            borderRadius: 2,
                                            minWidth: '300px'
                                        }}>
                                            <Box sx={{ 
                                                width: 20, 
                                                height: 20, 
                                                borderRadius: '50%', 
                                                background: '#3498db',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>
                                                {index + 1}
                                            </Box>
                                            <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                                                {step}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Fade>
                    </Box>
                </Fade>
            </DialogContent>
            
            {/* Footer */}
            <DialogActions sx={{ 
                justifyContent: 'center', 
                pb: 4, 
                pt: 2,
                background: 'rgba(255,255,255,0.3)'
            }}>
                <Zoom in={showContent} timeout={2000}>
                <Button
                    onClick={onClose}
                    variant="contained"
                        size="medium"
                    sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                            px: 5,
                            py: 0.8,
                            borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 'bold',
                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease',
                        '&:hover': {
                                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
                        }
                    }}
                >
                        {t('Understood')}
                </Button>
                </Zoom>
            </DialogActions>
        </Dialog>
    );
};

export default CancelDocumentsModal;